import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Save, Paperclip, X, Presentation, Library, BookMarked } from "lucide-react";
import api from "@/lib/api";
import { QuestionBankPicker } from "./QuestionBankPicker";

const inputCls = "w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-muted-foreground/50 focus:outline-none focus:border-cadmium/50 transition-colors";
const labelCls = "block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5";

export const CourseBuilder = ({ initial, onCreated, onCancel }) => {
  const editing = Boolean(initial?.id);
  const [form, setForm] = useState(
    initial
      ? { title: initial.title, category: initial.category, description: initial.description || "", pass_mark: initial.pass_mark }
      : { title: "", category: "Operations", description: "", pass_mark: 85 }
  );
  const [modules, setModules] = useState(
    initial?.modules?.map((m) => ({ title: m.title, video_url: m.video_url || "", slides_path: m.slides_path || "", slides_name: m.slides_name || "", lessons: m.lessons.map((l) => ({ title: l.title, body: l.body, duration_min: l.duration_min, video_url: l.video_url || "", attachment_path: l.attachment_path || "", attachment_name: l.attachment_name || "" })) })) || []
  );
  const [exam, setExam] = useState(initial?.exam?.map((q) => ({ q: q.q, options: [...q.options], answer: q.answer })) || []);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [bankOpen, setBankOpen] = useState(false);
  const [bankIds, setBankIds] = useState([]);
  const [savingBankQi, setSavingBankQi] = useState(null);
  const [bankMeta, setBankMeta] = useState({ category: "General", difficulty: "intermediate" });
  const [bankSaving, setBankSaving] = useState(false);

  const saveToBank = async (qi) => {
    const q = exam[qi];
    if (!q.q.trim() || q.options.some((o) => !o.trim())) { toast.error("Complete the question and all options first"); return; }
    setBankSaving(true);
    try {
      await api.post("/training/questions", { q: q.q, options: q.options, answer: Number(q.answer), ...bankMeta });
      toast.success("Saved to question bank", { description: `${bankMeta.category} · ${bankMeta.difficulty}` });
      setSavingBankQi(null);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not save to bank");
    } finally {
      setBankSaving(false);
    }
  };

  const insertFromBank = (q) => {
    setExam([...exam, { q: q.q, options: [...q.options], answer: q.answer, category: q.category, difficulty: q.difficulty }]);
    setBankIds([...bankIds, q.id]);
    setBankOpen(false);
    toast.success("Question inserted from bank", { description: `${q.category} · ${q.difficulty}` });
  };

  const attachFile = async (mi, li, file) => {
    if (!file) return;
    const key = `${mi}-${li}`;
    setUploading(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/assets", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLesson(mi, li, { attachment_path: res.data.storage_path, attachment_name: res.data.original_filename });
      toast.success("Attachment uploaded", { description: res.data.original_filename });
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Upload failed");
    } finally {
      setUploading("");
    }
  };

  const setModule = (mi, patch) => setModules(modules.map((m, i) => (i === mi ? { ...m, ...patch } : m)));
  const setLesson = (mi, li, patch) =>
    setModules(modules.map((m, i) => (i === mi ? { ...m, lessons: m.lessons.map((l, j) => (j === li ? { ...l, ...patch } : l)) } : m)));
  const setQuestion = (qi, patch) => setExam(exam.map((q, i) => (i === qi ? { ...q, ...patch } : q)));

  const attachSlides = async (mi, file) => {
    if (!file) return;
    setUploading(`m${mi}`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/assets", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setModule(mi, { slides_path: res.data.storage_path, slides_name: res.data.original_filename });
      toast.success("Slides uploaded", { description: res.data.original_filename });
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Upload failed");
    } finally {
      setUploading("");
    }
  };

  const submit = async () => {
    if (form.title.trim().length < 3) { toast.error("Course title is required"); return; }
    const dropped = modules.reduce(
      (n, m) => n + m.lessons.filter((l) => !l.title.trim() && (l.body.trim() || l.video_url?.trim() || l.attachment_path)).length,
      0
    );
    setSaving(true);
    try {
      const payload = {
        ...form,
        pass_mark: Number(form.pass_mark) || 85,
        modules: modules
          .filter((m) => m.title.trim())
          .map((m) => ({ ...m, lessons: m.lessons.filter((l) => l.title.trim()) })),
        exam: exam.filter((q) => q.q.trim() && q.options.every((o) => o.trim())).map((q) => ({ ...q, answer: Number(q.answer) })),
        published: true,
        bank_question_ids: bankIds,
      };
      if (editing) await api.patch(`/training/courses/${initial.id}`, payload);
      else await api.post("/training/courses", payload);
      toast.success(editing ? "Course updated" : "Course created", {
        description: editing ? "Existing employee progress is preserved." : undefined,
      });
      if (dropped > 0) {
        toast.info(`${dropped} lesson row(s) skipped`, { description: "Lessons need a title — untitled rows with content were not saved." });
      }
      onCreated?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === "string"
        ? d
        : Array.isArray(d)
          ? d.map((e) => `${(e.loc || []).filter((x) => x !== "body").join(" › ")}: ${e.msg}`).join(" · ")
          : "Connection error";
      toast.error(editing ? "Update failed" : "Create failed", { description: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="technical-card p-6 mb-8" data-testid="course-builder">
      <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900 mb-5">{editing ? `Edit Course — ${initial.title}` : "New Course"}</p>
      <div className="grid sm:grid-cols-3 gap-4 mb-4">
        <div className="sm:col-span-2">
          <label className={labelCls}>Title</label>
          <input data-testid="cb-title" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Fair Housing Essentials" />
        </div>
        <div>
          <label className={labelCls}>Category</label>
          <input data-testid="cb-category" className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div className="sm:col-span-2">
          <label className={labelCls}>Description</label>
          <input data-testid="cb-description" className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Pass mark %</label>
          <input data-testid="cb-passmark" type="number" min="1" max="100" className={inputCls} value={form.pass_mark} onChange={(e) => setForm({ ...form, pass_mark: e.target.value })} />
        </div>
      </div>

      <p className={labelCls}>Modules & Lessons</p>
      {modules.map((m, mi) => (
        <div key={mi} className="rounded-md border border-slate-200 bg-slate-50 p-4 mb-3" data-testid={`cb-module-${mi}`}>
          <div className="flex items-center gap-2 mb-3">
            <input className={inputCls} placeholder={`Module ${mi + 1} title`} value={m.title} onChange={(e) => setModule(mi, { title: e.target.value })} data-testid={`cb-module-${mi}-title`} />
            <button onClick={() => setModules(modules.filter((_, i) => i !== mi))} className="text-muted-foreground hover:text-sky-600 transition-colors shrink-0" aria-label="Remove module"><Trash2 size={15} /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-2 mb-3">
            <input className={inputCls} placeholder="Module video URL — YouTube or Loom (optional)" value={m.video_url || ""} onChange={(e) => setModule(mi, { video_url: e.target.value })} data-testid={`cb-module-${mi}-video`} />
            {m.slides_path ? (
              <span className="inline-flex items-center gap-2 self-center rounded-sm border border-moss/30 bg-moss/10 px-2 py-1 text-[10px] font-mono text-green-700" data-testid={`cb-module-${mi}-slides`}>
                <Presentation size={11} /> {m.slides_name}
                <button type="button" onClick={() => setModule(mi, { slides_path: "", slides_name: "" })} className="hover:text-white transition-colors" aria-label="Remove slides"><X size={11} /></button>
              </span>
            ) : (
              <>
                <input type="file" accept=".pdf,.ppt,.pptx" className="hidden" id={`cb-slides-${mi}`} onChange={(e) => { attachSlides(mi, e.target.files?.[0]); e.target.value = ""; }} />
                <label htmlFor={`cb-slides-${mi}`} data-testid={`cb-module-${mi}-slides-attach`} className="inline-flex items-center gap-1.5 self-center rounded-sm border border-slate-200 px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground hover:text-sky-600 hover:border-cadmium/40 transition-colors cursor-pointer w-fit">
                  {uploading === `m${mi}` ? <Loader2 size={11} className="animate-spin" /> : <Presentation size={11} />} Upload Slides (PDF / PPT)
                </label>
              </>
            )}
          </div>
          {m.lessons.map((l, li) => (
            <div key={li} className="ml-3 border-l border-slate-200 pl-3 mb-3">
              <div className="flex items-center gap-2 mb-2">
                <input className={inputCls} placeholder="Lesson title" value={l.title} onChange={(e) => setLesson(mi, li, { title: e.target.value })} data-testid={`cb-lesson-${mi}-${li}-title`} />
                <input type="number" min="1" className="w-20 shrink-0 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-muted-foreground/50 focus:outline-none focus:border-cadmium/50 transition-colors" title="Minutes" value={l.duration_min} onChange={(e) => setLesson(mi, li, { duration_min: Number(e.target.value) || 5 })} data-testid={`cb-lesson-${mi}-${li}-duration`} />
                <button onClick={() => setModule(mi, { lessons: m.lessons.filter((_, j) => j !== li) })} className="text-muted-foreground hover:text-sky-600 transition-colors shrink-0" aria-label="Remove lesson"><Trash2 size={14} /></button>
              </div>
              <textarea rows={3} className={`${inputCls} font-mono text-xs`} placeholder="Lesson content…" value={l.body} onChange={(e) => setLesson(mi, li, { body: e.target.value })} data-testid={`cb-lesson-${mi}-${li}-body`} />
              <input className={`${inputCls} mt-2 text-xs`} placeholder="Video URL — YouTube or Loom (optional)" value={l.video_url || ""} onChange={(e) => setLesson(mi, li, { video_url: e.target.value })} data-testid={`cb-lesson-${mi}-${li}-video`} />
              <div className="mt-2">
                {l.attachment_path ? (
                  <span className="inline-flex items-center gap-2 rounded-sm border border-moss/30 bg-moss/10 px-2 py-1 text-[10px] font-mono text-green-700" data-testid={`cb-lesson-${mi}-${li}-attachment`}>
                    <Paperclip size={11} /> {l.attachment_name}
                    <button type="button" onClick={() => setLesson(mi, li, { attachment_path: "", attachment_name: "" })} className="hover:text-white transition-colors" aria-label="Remove attachment"><X size={11} /></button>
                  </span>
                ) : (
                  <>
                    <input type="file" accept=".pdf,.txt,.csv,image/*" className="hidden" id={`cb-file-${mi}-${li}`} onChange={(e) => { attachFile(mi, li, e.target.files?.[0]); e.target.value = ""; }} />
                    <label htmlFor={`cb-file-${mi}-${li}`} data-testid={`cb-lesson-${mi}-${li}-attach`} className="inline-flex items-center gap-1.5 rounded-sm border border-slate-200 px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground hover:text-sky-600 hover:border-cadmium/40 transition-colors cursor-pointer">
                      {uploading === `${mi}-${li}` ? <Loader2 size={11} className="animate-spin" /> : <Paperclip size={11} />} Attach SOP / PDF
                    </label>
                  </>
                )}
              </div>
            </div>
          ))}
          <button onClick={() => setModule(mi, { lessons: [...m.lessons, { title: "", body: "", duration_min: 5, video_url: "", attachment_path: "", attachment_name: "" }] })} className="ml-3 text-[11px] font-mono uppercase tracking-wider text-sky-600 hover:text-white transition-colors" data-testid={`cb-module-${mi}-add-lesson`}>
            + Add lesson
          </button>
        </div>
      ))}
      <button onClick={() => setModules([...modules, { title: "", lessons: [], video_url: "", slides_path: "", slides_name: "" }])} className="mb-6 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-sky-600 hover:text-white transition-colors" data-testid="cb-add-module">
        <Plus size={13} /> Add module
      </button>

      <div className="flex items-center justify-between mb-1.5">
        <p className={`${labelCls} !mb-0`}>Exam Questions</p>
        <button type="button" onClick={() => setBankOpen(true)} className="inline-flex items-center gap-1.5 rounded-sm border border-cadmium/40 bg-cadmium/10 px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-wider text-sky-600 hover:bg-cadmium/20 transition-colors" data-testid="cb-open-bank">
          <Library size={11} /> Question Bank
        </button>
      </div>
      {exam.map((q, qi) => (
        <div key={qi} className="rounded-md border border-slate-200 bg-slate-50 p-4 mb-3" data-testid={`cb-question-${qi}`}>
          <div className="flex items-center gap-2 mb-2">
            <input className={inputCls} placeholder={`Question ${qi + 1}`} value={q.q} onChange={(e) => setQuestion(qi, { q: e.target.value })} data-testid={`cb-q-${qi}-text`} />
            <button onClick={() => { setSavingBankQi(savingBankQi === qi ? null : qi); setBankMeta({ category: form.category || "General", difficulty: "intermediate" }); }} className={`transition-colors shrink-0 ${savingBankQi === qi ? "text-sky-600" : "text-muted-foreground hover:text-sky-600"}`} aria-label="Save to question bank" title="Save to question bank" data-testid={`cb-q-${qi}-save-bank`}><BookMarked size={15} /></button>
            <button onClick={() => setExam(exam.filter((_, i) => i !== qi))} className="text-muted-foreground hover:text-sky-600 transition-colors shrink-0" aria-label="Remove question"><Trash2 size={15} /></button>
          </div>
          {savingBankQi === qi && (
            <div className="flex flex-wrap items-center gap-2 mb-3 rounded-sm border border-cadmium/20 bg-cadmium/5 px-3 py-2" data-testid={`cb-q-${qi}-bank-form`}>
              <span className="text-[10px] font-mono uppercase tracking-wider text-sky-600">Save to bank:</span>
              <input className="w-36 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-900 placeholder:text-muted-foreground/50 focus:outline-none focus:border-cadmium/50" placeholder="Category" value={bankMeta.category} onChange={(e) => setBankMeta({ ...bankMeta, category: e.target.value })} data-testid={`cb-q-${qi}-bank-cat`} />
              <select className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900 focus:outline-none focus:border-cadmium/50" value={bankMeta.difficulty} onChange={(e) => setBankMeta({ ...bankMeta, difficulty: e.target.value })} data-testid={`cb-q-${qi}-bank-diff`}>
                <option value="easy">Easy</option>
                <option value="intermediate">Intermediate</option>
                <option value="hard">Hard</option>
              </select>
              <button onClick={() => saveToBank(qi)} disabled={bankSaving} className="inline-flex items-center gap-1 rounded-md bg-cadmium px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-white hover:bg-cadmium-hover transition-colors disabled:opacity-60" data-testid={`cb-q-${qi}-bank-confirm`}>
                {bankSaving ? <Loader2 size={10} className="animate-spin" /> : <BookMarked size={10} />} Save
              </button>
            </div>
          )}
          {q.options.map((o, oi) => (
            <label key={oi} className="flex items-center gap-2 mb-1.5">
              <input type="radio" name={`cb-q-${qi}`} checked={q.answer === oi} onChange={() => setQuestion(qi, { answer: oi })} className="accent-[#2E9CFF]" data-testid={`cb-q-${qi}-answer-${oi}`} />
              <input className={inputCls} placeholder={`Option ${oi + 1} (radio = correct answer)`} value={o} onChange={(e) => setQuestion(qi, { options: q.options.map((x, j) => (j === oi ? e.target.value : x)) })} data-testid={`cb-q-${qi}-opt-${oi}`} />
            </label>
          ))}
        </div>
      ))}
      <button onClick={() => setExam([...exam, { q: "", options: ["", "", "", ""], answer: 0 }])} className="mb-6 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-sky-600 hover:text-white transition-colors" data-testid="cb-add-question">
        <Plus size={13} /> Add question
      </button>

      <div className="flex gap-3">
        <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 rounded-md bg-cadmium px-5 py-2.5 text-xs font-mono uppercase tracking-widest text-white hover:bg-cadmium-hover transition-colors disabled:opacity-60" data-testid="cb-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {editing ? "Save Changes" : "Create Course"}
        </button>
        <button onClick={onCancel} className="text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-slate-900 transition-colors" data-testid="cb-cancel">Cancel</button>
      </div>
      {bankOpen && <QuestionBankPicker onInsert={insertFromBank} onClose={() => setBankOpen(false)} />}
    </div>
  );
};
